// Massachusetts Institute of Technology: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - MIT's Common Data Set 2025-26, published as a web page by MIT
//    Institutional Research (C1 applicants/admits/enrollees for Fall 2025;
//    C8 test policy for Fall 2027; C21/C22 early plans; G1/G5 2026-2027
//    costs). C1's residency table is left blank, so no residency rows.
//  - mitadmissions.org first-year pages (deadlines and requirements, early vs
//    regular, tests and scores). MIT calls its rounds Early Action and
//    Regular Action.
//  - MIT Student Financial Services, cost of attendance 2026-2027. Its items
//    and $92,760 total match CDS G1/G5; MIT adds a travel allowance by home
//    address and publishes no single figure for it (CDS G5: "varies").
//  - College Scorecard API, school id 166683 (net price by family income).
//    The $0 to $30,000 band is negative in the Department's own data.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'MIT Common Data Set 2025-2026',
        url: 'https://ir.mit.edu/projects/2025-26-common-data-set/',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'MIT Admissions, Deadlines and Requirements',
        url: 'https://mitadmissions.org/apply/firstyear/deadlines-requirements/',
        read: '2026-09-30',
    },
    earlyAction: {
        label: 'MIT Admissions, Early vs Regular',
        url: 'https://mitadmissions.org/apply/firstyear/early-vs-regular/',
        read: '2026-09-30',
    },
    tests: {
        label: 'MIT Admissions, Tests and Scores',
        url: 'https://mitadmissions.org/apply/firstyear/tests-scores/',
        read: '2026-09-30',
    },
    cost: {
        label: 'MIT Student Financial Services, Cost of Attendance',
        url: 'https://sfs.mit.edu/undergraduate-students/the-cost-of-attendance/coa/',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?166683-Massachusetts-Institute-of-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'massachusetts-institute-of-technology',
    kbId: 'massachusetts_institute_of_technology',
    name: 'Massachusetts Institute of Technology',
    shortName: 'MIT',
    location: 'Cambridge, MA',
    control: 'Private',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (MIT brand site); decoration only.
    branding: {
        primary: '#750014',
        secondary: '#8B959E',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/massachusetts_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 29281,
        admitted: 1334,
        enrolled: 1152,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'MIT\'s Common Data Set reports one first-year acceptance rate for the whole institute, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'MIT has no early decision plan, and the Common Data Set does not report how many applied or were admitted in early action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Action', value: 'January 4' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Regular Action', deadline: 'January 4', decision: 'Mid-March', binding: false },
        ],
        note: 'The financial aid application is due separately: November 30 for Early Action and February 15 for Regular Action.',
        source: 'deadlines',
        binding: 'MIT\'s Early Action is not single-choice or binding: MIT places no limits on where else you apply, and you do not have to attend if admitted. If you also apply early to a school with its own restriction, MIT asks you to follow that school\'s rules. You can apply only once per entry year, so if you are denied in Early Action you cannot apply in Regular Action.',
        bindingSource: 'earlyAction',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'MIT requires the SAT or the ACT. You self-report your scores on the application, and MIT verifies them if you enroll. Take the tests before November 30 for Early Action, or before December 31 for Regular Action.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required to be considered for fall 2027 admission.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 66720 },
            { item: 'Student life fee', amount: 420 },
            { item: 'Housing', amount: 14090 },
            { item: 'Food', amount: 8104 },
            { item: 'Books, course materials, supplies and equipment', amount: 930 },
            { item: 'Personal expenses', amount: 2496 },
            { item: 'Travel', amount: null },
        ],
        total: 92760,
        note: 'MIT adds a travel allowance based on where you live, so travel is not in the $92,760 total.',
        source: 'cost',
        netPrice: {
            average: 14815,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: null },
                { income: '$48,001 to $75,000', amount: 4283 },
                { income: '$75,001 to $110,000', amount: 8020 },
                { income: 'Over $110,000', amount: 50747 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 and $30,001 to $48,000 bands. Your own price comes from MIT\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
